/**
 * GraphQL Query definitions
 * 
 * These queries match the AppSync GraphQL schema defined in infrastructure/graphql/schema.graphql
 */

export const getApplicant = /* GraphQL */ `
  query GetApplicant($id: ID!) {
    getApplicant(id: $id) {
      id
      email
      fullName
      homePhone
      mobilePhone
      createdAt
      updatedAt
    }
  }
`;

export const getApplicantYearProfile = /* GraphQL */ `
  query GetApplicantYearProfile($applicantId: ID!, $competitionYear: Int!) {
    getApplicantYearProfile(applicantId: $applicantId, competitionYear: $competitionYear) {
      applicantId
      competitionYear
      applicantType
      submissionStatus
      phase1Data
      stageFormData
      interviewInfoData
      placementQuestionsData
      placementQuestionsLocked
      profileLocked
      outstandingFees
      digitalSignature
      signedAt
      directorNotes
      createdAt
      updatedAt
    }
  }
`;

export const listApplicants = /* GraphQL */ `
  query ListApplicants($filter: ApplicantFilterInput, $limit: Int, $nextToken: String) {
    listApplicants(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        email
        fullName
        homePhone
        mobilePhone
        lastLoginAt
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const getActiveFormSchema = /* GraphQL */ `
  query GetActiveFormSchema($formType: FormType!, $applicantType: ApplicantType!, $competitionYear: Int!) {
    getActiveFormSchema(formType: $formType, applicantType: $applicantType, competitionYear: $competitionYear) {
      id
      formType
      applicantType
      competitionYear
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const listFormFields = /* GraphQL */ `
  query ListFormFields($formSchemaId: ID!) {
    listFormFields(formSchemaId: $formSchemaId) {
      id
      formSchemaId
      label
      fieldType
      required
      wordLimit
      characterLimit
      options
      displayOrder
      immutableOnSubmit
      thirdPersonRequired
      applicantTypeVisibility
      visibleDivisionKeys
    }
  }
`;

export const listVolunteerActivities = /* GraphQL */ `
  query ListVolunteerActivities($applicantId: ID!) {
    listVolunteerActivities(applicantId: $applicantId) {
      id
      applicantId
      organizationName
      activityDate
      hoursContributed
      description
      verifiedByDirector
      createdAt
    }
  }
`;

export const listDocumentReferences = /* GraphQL */ `
  query ListDocumentReferences($applicantId: ID!, $competitionYear: Int!) {
    listDocumentReferences(applicantId: $applicantId, competitionYear: $competitionYear) {
      id
      applicantId
      competitionYear
      documentType
      s3Key
      fileName
      contentType
      uploadedAt
    }
  }
`;

export const getBrandingConfig = /* GraphQL */ `
  query GetBrandingConfig($competitionYear: Int!) {
    getBrandingConfig(competitionYear: $competitionYear) {
      competitionYear
      pageantName
      logoS3Key
      primaryColor
      accentColor
      headerText
      updatedAt
    }
  }
`;

export const getPublicProfile = /* GraphQL */ `
  query GetPublicProfile($applicantId: ID!, $competitionYear: Int!) {
    getPublicProfile(applicantId: $applicantId, competitionYear: $competitionYear) {
      applicantId
      competitionYear
      fullName
      applicantType
      headshot
      bio
      platformStatement
      interviewHighlights
    }
  }
`;

export const getDocumentDownloadUrl = /* GraphQL */ `
  query GetDocumentDownloadUrl($documentId: ID!) {
    getDocumentDownloadUrl(documentId: $documentId)
  }
`;

export const listApplicantsWithProfiles = /* GraphQL */ `
  query ListApplicantsWithProfiles($filter: ApplicantFilterInput, $limit: Int, $nextToken: String) {
    listApplicants(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        email
        fullName
        homePhone
        mobilePhone
        createdAt
        updatedAt
        yearProfiles {
          competitionYear
          applicantType
          submissionStatus
          outstandingFees
          profileLocked
          createdAt
        }
      }
      nextToken
    }
  }
`;

export const listPendingVerifications = /* GraphQL */ `
  query ListPendingVerifications {
    listPendingVerifications {
      id
      applicantId
      organizationName
      activityDate
      hoursContributed
      description
      verifiedByDirector
      createdAt
    }
  }
`;

export const getFormSchema = /* GraphQL */ `
  query GetFormSchema($id: ID!) {
    getFormSchema(id: $id) {
      id
      formType
      applicantType
      competitionYear
      isActive
      createdAt
      updatedAt
      fields {
        id
        formSchemaId
        label
        fieldType
        required
        wordLimit
        characterLimit
        options
        displayOrder
        immutableOnSubmit
        thirdPersonRequired
        applicantTypeVisibility
        visibleDivisionKeys
      }
    }
  }
`;

export const listApplicantYearProfiles = /* GraphQL */ `
  query ListApplicantYearProfiles($applicantId: ID!) {
    getApplicant(id: $applicantId) {
      id
      email
      fullName
      yearProfiles {
        applicantId
        competitionYear
        applicantType
        submissionStatus
        outstandingFees
        profileLocked
        placementQuestionsLocked
        directorNotes
        createdAt
        updatedAt
      }
    }
  }
`;

// Returning contestant detection queries
// Requirements: 24.1, 24.2 - Check for existing Applicant records by email

export const getApplicantByEmail = /* GraphQL */ `
  query GetApplicantByEmail($email: String!) {
    getApplicantByEmail(email: $email) {
      id
      email
      fullName
      homePhone
      mobilePhone
      createdAt
      updatedAt
    }
  }
`;

export const listApplicantYearProfilesForApplicant = /* GraphQL */ `
  query ListApplicantYearProfilesForApplicant($applicantId: ID!) {
    listApplicantYearProfiles(applicantId: $applicantId) {
      applicantId
      competitionYear
      applicantType
      submissionStatus
      phase1Data
      stageFormData
      interviewInfoData
      placementQuestionsData
      placementQuestionsLocked
      profileLocked
      outstandingFees
      digitalSignature
      signedAt
      directorNotes
      createdAt
      updatedAt
    }
  }
`;

// Combined query for returning contestant detection
// Checks if applicant exists by email and retrieves all their year profiles
export const getReturningContestantData = /* GraphQL */ `
  query GetReturningContestantData($email: String!) {
    getApplicantByEmail(email: $email) {
      id
      email
      fullName
      homePhone
      mobilePhone
      createdAt
      updatedAt
      yearProfiles {
        applicantId
        competitionYear
        applicantType
        submissionStatus
        outstandingFees
        profileLocked
        placementQuestionsLocked
        createdAt
        updatedAt
      }
    }
  }
`;


// Query to fetch applicant by Cognito sub for dashboard
// Requirements: 7.1, 10.2, 10.5
export const getApplicantByCognitoSub = /* GraphQL */ `
  query GetApplicantByCognitoSub($cognitoSub: String!) {
    getApplicantByCognitoSub(cognitoSub: $cognitoSub) {
      id
      email
      fullName
      cognitoSub
      yearProfiles {
        applicantId
        competitionYear
        applicantType
        submissionStatus
        profileLocked
        placementQuestionsLocked
      }
    }
  }
`;

// Admin user management queries
// Requirements: 9.1, 9.4
export const listCognitoUsers = /* GraphQL */ `
  query ListCognitoUsers($groupName: String!) {
    listCognitoUsers(groupName: $groupName) {
      email
      fullName
      status
      createdAt
    }
  }
`;

// Profile photo download URL query
// Requirements: 3.5, 7.1
export const getProfilePhotoDownloadUrl = /* GraphQL */ `
  query GetProfilePhotoDownloadUrl($s3Key: String!) {
    getProfilePhotoDownloadUrl(s3Key: $s3Key)
  }
`;

// Inquiry listing query (admin only, Cognito auth)
// Requirements: 7.4, 8.3, 10.5
export const listInquiries = /* GraphQL */ `
  query ListInquiries($limit: Int, $nextToken: String) {
    listInquiries(limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        email
        subject
        message
        status
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Email template queries (admin only, Cognito auth)
// Requirements: 1.3, 1.4
export const listEmailTemplates = /* GraphQL */ `
  query ListEmailTemplates {
    listEmailTemplates {
      id
      templateName
      subject
      body
      createdAt
      updatedAt
      lastEditedBy
    }
  }
`;

export const getEmailTemplate = /* GraphQL */ `
  query GetEmailTemplate($id: ID!) {
    getEmailTemplate(id: $id) {
      id
      templateName
      subject
      body
      createdAt
      updatedAt
      lastEditedBy
    }
  }
`;

// Inquiry response listing query (admin only, Cognito auth)
// Requirements: 1.4, 7.4
export const listInquiryResponses = /* GraphQL */ `
  query ListInquiryResponses($inquiryId: ID!) {
    listInquiryResponses(inquiryId: $inquiryId) {
      id
      inquiryId
      adminEmail
      adminName
      responseText
      createdAt
      updatedAt
    }
  }
`;

// System log query (admin only, Cognito auth)
// Audit logging for admin actions
export const listSystemLogEntries = /* GraphQL */ `
  query ListSystemLogEntries($limit: Int) {
    listSystemLogEntries(limit: $limit) {
      id
      timestamp
      action
      performedBy
      targetName
      targetEmail
      targetId
      details
    }
  }
`;

export const listCalendarEvents = /* GraphQL */ `
  query ListCalendarEvents($competitionYear: Int!) {
    listCalendarEvents(competitionYear: $competitionYear) {
      id
      title
      date
      time
      endDate
      endTime
      allDay
      location
      description
      type
      competitionYear
      isCountdownAnchor
      createdBy
      createdAt
      updatedAt
    }
  }
`;

// Countdown anchor query — returns the single anchor event for a year (or null).
// Requirements: 1.4, 4.1 (countdown-anchor)
export const getCountdownAnchor = /* GraphQL */ `
  query GetCountdownAnchor($competitionYear: Int!) {
    getCountdownAnchor(competitionYear: $competitionYear) {
      id
      title
      date
      time
      allDay
      competitionYear
      isCountdownAnchor
    }
  }
`;

// User queries (unified user identity)
// Requirements: 8.1, 8.2, 8.3, 8.4, 8.5
export const getUser = /* GraphQL */ `
  query GetUser($cognitoSub: String!) {
    getUser(cognitoSub: $cognitoSub) {
      cognitoSub
      email
      fullName
      role
      profilePhotoS3Key
      createdAt
      updatedAt
      lastLoginAt
    }
  }
`;

export const getUserByEmail = /* GraphQL */ `
  query GetUserByEmail($email: String!) {
    getUserByEmail(email: $email) {
      cognitoSub
      email
      fullName
      role
      profilePhotoS3Key
      createdAt
      updatedAt
      lastLoginAt
    }
  }
`;

export const listUsers = /* GraphQL */ `
  query ListUsers($role: String, $limit: Int, $nextToken: String) {
    listUsers(role: $role, limit: $limit, nextToken: $nextToken) {
      items {
        cognitoSub
        email
        fullName
        role
        profilePhotoS3Key
        createdAt
        updatedAt
        lastLoginAt
      }
      nextToken
    }
  }
`;

// Channel membership queries — REMOVED by stream-chat-messaging-migration.
// Stream Chat is the source of truth for per-user, per-channel read state.

// Public query for listing approved contestants on the Contestants page
export const listApprovedContestants = /* GraphQL */ `
  query ListApprovedContestants($competitionYear: Int!) {
    listApprovedContestants(competitionYear: $competitionYear) {
      fullName
      approvedTitle
      applicantType
      profilePhotoS3Key
    }
  }
`;

// Division registry — list the year's divisions for data-driven public-site
// tabs and intake category selection (division-registry spec).
// Reads carry @aws_api_key in the schema so the public site can render
// unauthenticated. Requirements: 5.1, 5.2, 5.6
export const listDivisions = /* GraphQL */ `
  query ListDivisions($competitionYear: Int!) {
    listDivisions(competitionYear: $competitionYear) {
      id
      competitionYear
      key
      label
      titlePrefix
      displayOrder
      appliesToApplications
      appliesToJudging
      active
      createdAt
      updatedAt
    }
  }
`;

// Division registry — list the contestant-number assignments for a single
// (competitionYear, divisionKey) so the Admin number-assignment UI can show
// each contestant's current number. Admin-gated (@aws_cognito_user_pools only)
// in the schema. Requirements: 4.1, 4.7, 4.8, 8.3
export const listContestantNumbers = /* GraphQL */ `
  query ListContestantNumbers($competitionYear: Int!, $divisionKey: String!) {
    listContestantNumbers(competitionYear: $competitionYear, divisionKey: $divisionKey) {
      competitionYear
      divisionKey
      personId
      number
      assignedAt
    }
  }
`;

// ========================================
// Season-Scoped Title Catalog reads (season-scoped-title-registry spec)
//
// The director-managed, per-competitionYear Title catalog. Unlike Division
// reads, Title reads carry ONLY @aws_cognito_user_pools in the schema (never
// @aws_api_key): the catalog is read by authenticated contestants (to select
// title preferences) and Admin-gated Directors (to manage). `listTitles`
// returns titles ordered by `displayOrder`. Requirements: 1.7, 2.1, 5.1
// ========================================

// Shared Title selection set, reused across the Title queries. Mirrors the
// division fields plus the optional applicant/division scope on Title.
const TITLE_FIELDS = /* GraphQL */ `
  id
  competitionYear
  key
  label
  active
  displayOrder
  applicantType
  divisionKey
  createdAt
  updatedAt
`;

export const listTitles = /* GraphQL */ `
  query ListTitles($competitionYear: Int!) {
    listTitles(competitionYear: $competitionYear) {
      ${TITLE_FIELDS}
    }
  }
`;

export const getTitle = /* GraphQL */ `
  query GetTitle($competitionYear: Int!, $key: String!) {
    getTitle(competitionYear: $competitionYear, key: $key) {
      ${TITLE_FIELDS}
    }
  }
`;

// ========================================
// Payment Queries
// ========================================

export const listFees = /* GraphQL */ `
  query ListFees($competitionYear: Int!) {
    listFees(competitionYear: $competitionYear) {
      feeId
      competitionYear
      name
      description
      amount
      statusTrigger
      required
      dueDate
      active
      createdAt
      updatedAt
    }
  }
`;

export const listContestantFees = /* GraphQL */ `
  query ListContestantFees($applicantId: ID!, $competitionYear: Int!) {
    listContestantFees(applicantId: $applicantId, competitionYear: $competitionYear) {
      applicantId
      competitionYear
      feeId
      feeName
      amount
      originalAmount
      statusTrigger
      required
      status
      paymentIntentId
      paidAt
      dueDate
      discountRuleId
      discountName
      createdAt
    }
  }
`;

export const listPayments = /* GraphQL */ `
  query ListPayments($applicantId: ID, $competitionYear: Int, $limit: Int, $nextToken: String) {
    listPayments(applicantId: $applicantId, competitionYear: $competitionYear, limit: $limit, nextToken: $nextToken) {
      paymentIntentId
      applicantId
      competitionYear
      amount
      currency
      status
      feeIds
      discountRuleId
      receiptUrl
      createdAt
      paidAt
      refundedAt
    }
  }
`;

export const listDiscountRules = /* GraphQL */ `
  query ListDiscountRules($competitionYear: Int!) {
    listDiscountRules(competitionYear: $competitionYear) {
      ruleId
      competitionYear
      name
      type
      value
      appliesTo
      targetFeeId
      targetStatus
      eligibility
      eligibilityCondition
      code
      maxUses
      currentUses
      expiresAt
      active
      createdAt
    }
  }
`;

export const getStripeStatus = /* GraphQL */ `
  query GetStripeStatus {
    getStripeStatus {
      connected
      dashboardUrl
    }
  }
`;

// Instagram token info query (Phyllo-based)
// Requirements: 4.1, 13.5
export const getInstagramTokenInfo = /* GraphQL */ `
  query GetInstagramTokenInfo($cognitoSub: String!) {
    getInstagramTokenInfo(cognitoSub: $cognitoSub) {
      cognitoSub
      instagramUsername
      phylloUserId
      phylloAccountId
      linkStatus
      linkedAt
    }
  }
`;

// Individual contestant Instagram metrics query
// Requirements: 10.1, 10.2, 10.3, 10.4, 10.5
export const getLatestInstagramMetrics = /* GraphQL */ `
  query GetLatestInstagramMetrics($cognitoSub: String!) {
    getLatestInstagramMetrics(cognitoSub: $cognitoSub) {
      cognitoSub
      snapshotDate
      instagramUsername
      followerCount
      engagementRate
      totalPosts
      averageLikesPerPost
      averageCommentsPerPost
      reachTotal
      impressionsTotal
      fetchedAt
    }
  }
`;

// Instagram metrics leaderboard query (admin only)
// Requirements: 9.1, 9.2, 9.3, 9.4, 9.5
export const listInstagramMetricsLeaderboard = /* GraphQL */ `
  query ListInstagramMetricsLeaderboard($snapshotDate: String!, $sortBy: String, $limit: Int) {
    listInstagramMetricsLeaderboard(snapshotDate: $snapshotDate, sortBy: $sortBy, limit: $limit) {
      items {
        cognitoSub
        snapshotDate
        instagramUsername
        followerCount
        engagementRate
        totalPosts
        averageLikesPerPost
        averageCommentsPerPost
        reachTotal
        impressionsTotal
        fetchedAt
      }
    }
  }
`;

// ========================================
// Season-Scoped Instagram Insights Queries
// Requirements: 5.1, 7.1, 9.1, 10.1, 16.1, 16.7, 16.13
// ========================================

export const listSeasonLeaderboard = /* GraphQL */ `
  query ListSeasonLeaderboard(
    $competitionYear: Int!
    $sortBy: SeasonLeaderboardSortKey
    $limit: Int
    $nextToken: String
  ) {
    listSeasonLeaderboard(
      competitionYear: $competitionYear
      sortBy: $sortBy
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        cognitoSub
        fullName
        instagramUsername
        followersGained
        postsMadeThisSeason
        reachThisSeason
        impressionsThisSeason
        baselineCapturedAt
        lastSnapshotAt
      }
      nextToken
    }
  }
`;

export const getContestantSeasonInsights = /* GraphQL */ `
  query GetContestantSeasonInsights($competitionYear: Int!) {
    getContestantSeasonInsights(competitionYear: $competitionYear) {
      cognitoSub
      competitionYear
      instagramUsername
      followersGained
      postsMadeThisSeason
      reachThisSeason
      impressionsThisSeason
      baselineCapturedAt
      lastSnapshotAt
      rankByFollowersGained
      totalContestants
    }
  }
`;

export const listSeasonPeerLeaderboard = /* GraphQL */ `
  query ListSeasonPeerLeaderboard(
    $competitionYear: Int!
    $sortBy: SeasonLeaderboardSortKey
    $limit: Int
    $nextToken: String
  ) {
    listSeasonPeerLeaderboard(
      competitionYear: $competitionYear
      sortBy: $sortBy
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        rank
        fullName
        instagramUsername
        followersGained
        postsMadeThisSeason
        reachThisSeason
        impressionsThisSeason
        sparklinePoints {
          snapshotDate
          value
        }
      }
      nextToken
    }
  }
`;

export const getProgramAggregateInsights = /* GraphQL */ `
  query GetProgramAggregateInsights($competitionYear: Int!) {
    getProgramAggregateInsights(competitionYear: $competitionYear) {
      competitionYear
      contestantCount
      titleholderCount
      totalFollowersAcrossProgram
      totalFollowersGainedContestantsOnly
      totalPostsThisSeason
      totalReachThisSeason
      totalImpressionsThisSeason
      generatedAt
      lastSnapshotDate
      titleholders {
        cognitoSub
        fullName
        title
        profilePhotoS3Key
        instagramUsername
        followerCount
      }
    }
  }
`;

export const listSeasonGrowthSeries = /* GraphQL */ `
  query ListSeasonGrowthSeries(
    $competitionYear: Int!
    $metric: SeasonLeaderboardSortKey!
    $cognitoSubs: [String!]
    $limit: Int
  ) {
    listSeasonGrowthSeries(
      competitionYear: $competitionYear
      metric: $metric
      cognitoSubs: $cognitoSubs
      limit: $limit
    ) {
      items {
        cognitoSub
        fullName
        instagramUsername
        points {
          snapshotDate
          value
        }
      }
    }
  }
`;

export const getProgramReachTimeseries = /* GraphQL */ `
  query GetProgramReachTimeseries($competitionYear: Int!) {
    getProgramReachTimeseries(competitionYear: $competitionYear) {
      competitionYear
      weeks {
        weekStartDate
        contestantReach
        titleholderReach
      }
    }
  }
`;

export const getProgramPostsPerWeek = /* GraphQL */ `
  query GetProgramPostsPerWeek($competitionYear: Int!) {
    getProgramPostsPerWeek(competitionYear: $competitionYear) {
      competitionYear
      weeks {
        weekStartDate
        totalPosts
      }
    }
  }
`;

// ========================================
// Pageant Year Queries (messaging-season-scoping)
// Requirements: 3.1, 3.2
// ========================================

export const listPageantYears = /* GraphQL */ `
  query ListPageantYears {
    listPageantYears {
      competitionYear
      displayName
      status
      archiveAt
      launchedAt
      archivedAt
      createdAt
      updatedAt
    }
  }
`;

export const getActivePageantYear = /* GraphQL */ `
  query GetActivePageantYear {
    getActivePageantYear {
      competitionYear
      displayName
      status
      archiveAt
      launchedAt
    }
  }
`;

// ========================================
// Stream Chat — Conversation Initiation Queries
// Requirements: 3.1, 11.1 (stream-chat-conversation-initiation)
// ========================================

export const LIST_DM_CANDIDATES = /* GraphQL */ `
  query ListDMCandidates($search: String, $limit: Int, $nextToken: String) {
    listDMCandidates(search: $search, limit: $limit, nextToken: $nextToken) {
      items {
        cognitoSub
        email
        fullName
        role
        profilePhotoS3Key
      }
      nextToken
    }
  }
`;

// ========================================
// Messaging Sub-Navigation (spec 2)
// ========================================

export const LIST_BOOKMARKS = /* GraphQL */ `
  query ListBookmarks($channelCid: String, $limit: Int, $nextToken: String) {
    listBookmarks(channelCid: $channelCid, limit: $limit, nextToken: $nextToken) {
      items {
        bookmarkKey
        cognitoSub
        channelCid
        messageId
        createdAt
        competitionYear
        messageSnapshot {
          authorCognitoSub
          authorName
          text
          createdAt
          attachmentCount
        }
      }
      nextToken
    }
  }
`;

export const LIST_CHANNEL_ATTACHMENTS = /* GraphQL */ `
  query ListChannelAttachments($channelCid: String!, $limit: Int, $nextToken: String) {
    listChannelAttachments(channelCid: $channelCid, limit: $limit, nextToken: $nextToken) {
      items {
        attachmentId
        channelCid
        messageId
        messageCid
        sharedByCognitoSub
        sharedByDisplayName
        filename
        mimeType
        extension
        sizeBytes
        url
        kind
        createdAt
        competitionYear
      }
      nextToken
    }
  }
`;

// ---------------------------------------------------------------------------
// Messaging Activity + Drafts (spec 3)

export const LIST_USER_ACTIVITY = /* GraphQL */ `
  query ListUserActivity($limit: Int, $nextToken: String, $unreadOnly: Boolean) {
    listUserActivity(limit: $limit, nextToken: $nextToken, unreadOnly: $unreadOnly) {
      items {
        eventId
        cognitoSub
        kind
        actorCognitoSub
        actorDisplayName
        channelCid
        channelDisplayName
        messageId
        messagePreview
        reactionType
        competitionYear
        createdAt
        readAt
      }
      nextToken
      unreadCount
    }
  }
`;

export const LIST_DRAFTS = /* GraphQL */ `
  query ListDrafts($limit: Int, $nextToken: String) {
    listDrafts(limit: $limit, nextToken: $nextToken) {
      items {
        cognitoSub
        channelCid
        channelDisplayName
        bodyMarkdown
        pendingAttachments {
          tempId
          filename
          sizeBytes
          mimeType
          url
          status
          error
        }
        mentionedUserIds
        competitionYear
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_DRAFT = /* GraphQL */ `
  query GetDraft($channelCid: String!) {
    getDraft(channelCid: $channelCid) {
      cognitoSub
      channelCid
      channelDisplayName
      bodyMarkdown
      pendingAttachments {
        tempId
        filename
        sizeBytes
        mimeType
        url
        status
        error
      }
      mentionedUserIds
      competitionYear
      createdAt
      updatedAt
    }
  }
`;

// ========================================
// Herald Feed Queries (herald-feed spec)
// Community bulletin feed — all signed-in only.
// Requirements: 2.1
// ========================================

export const heraldFeed = /* GraphQL */ `
  query HeraldFeed($filter: HeraldFeedFilter, $limit: Int, $nextToken: String) {
    heraldFeed(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        authorCognitoSub
        authorName
        authorRole
        authorPhotoS3Key
        text
        imageS3Keys
        link {
          url
          title
          domain
          thumbnailS3Key
        }
        file {
          s3Key
          fileName
          contentType
          sizeBytes
        }
        taggedUsers {
          cognitoSub
          name
          photoS3Key
        }
        pinned
        likeCount
        liked
        bookmarked
        createdAt
        editedAt
      }
      nextToken
      counts {
        all
        pinned
        directors
      }
    }
  }
`;

export const heraldPost = /* GraphQL */ `
  query HeraldPost($id: ID!) {
    heraldPost(id: $id) {
      id
      authorCognitoSub
      authorName
      authorRole
      authorPhotoS3Key
      text
      imageS3Keys
      link {
        url
        title
        domain
        thumbnailS3Key
      }
      file {
        s3Key
        fileName
        contentType
        sizeBytes
      }
      taggedUsers {
        cognitoSub
        name
        photoS3Key
      }
      pinned
      likeCount
      liked
      bookmarked
      createdAt
      editedAt
    }
  }
`;

// ========================================
// Director Document Library Queries
// (director-document-library spec)
//
// Permissions are computed, not stored: every document is anchored to a domain
// entity and visibility is a pure function of the caller's role/identity and
// that anchor. Director browse queries surface only administered scopes; the
// download choke point authorizes every read before issuing a short-lived URL.
// Requirements: 1.x, 2.x, 3.x, 6.x, 7.x
// ========================================

// Top-level browse grouping — administered Seasons newest→oldest (R1.2, R1.3).
export const listDirectorDocumentSeasons = /* GraphQL */ `
  query ListDirectorDocumentSeasons {
    listDirectorDocumentSeasons {
      competitionYear
      label
    }
  }
`;

// Competitions within an administered Season (R1.3); hasDivisions drives the
// interposed Division level (R1.6, R1.8).
export const listDirectorDocumentCompetitions = /* GraphQL */ `
  query ListDirectorDocumentCompetitions($competitionYear: Int!) {
    listDirectorDocumentCompetitions(competitionYear: $competitionYear) {
      competitionId
      name
      hasDivisions
    }
  }
`;

// Divisions defined for a Competition (R1.6).
export const listDirectorDocumentDivisions = /* GraphQL */ `
  query ListDirectorDocumentDivisions($competitionId: ID!) {
    listDirectorDocumentDivisions(competitionId: $competitionId) {
      divisionKey
      label
    }
  }
`;

// Contestants within a Competition/Division, paginated at ≤50 (R1.4, R1.9, R1.10).
export const listDirectorDocumentContestants = /* GraphQL */ `
  query ListDirectorDocumentContestants(
    $competitionYear: Int!
    $competitionId: ID
    $divisionKey: String
    $cursor: String
  ) {
    listDirectorDocumentContestants(
      competitionYear: $competitionYear
      competitionId: $competitionId
      divisionKey: $divisionKey
      cursor: $cursor
    ) {
      items {
        personId
        applicantId
        displayName
        divisionKey
      }
      nextCursor
      pageNumber
      totalPages
    }
  }
`;

// Contestant_Documents read THROUGH DocumentReference (R1.4, R1.5, R2.x).
// Nullable fields render an explicit "unavailable" placeholder; fileAvailable
// gates the download control (R2.5, R2.6, R2.7).
export const listContestantDocuments = /* GraphQL */ `
  query ListContestantDocuments($personId: ID!, $competitionYear: Int!, $cursor: String) {
    listContestantDocuments(personId: $personId, competitionYear: $competitionYear, cursor: $cursor) {
      items {
        documentId
        fileName
        contentType
        sizeBytes
        uploadedAt
        sourceSlot
        submissionStatus
        approvalStatus
        catalogEntryId
        fileAvailable
      }
      nextCursor
      pageNumber
      totalPages
    }
  }
`;

// Library_Documents anchored to a Season/Competition, newest→oldest (R6.1).
export const listLibraryDocumentsByAnchor = /* GraphQL */ `
  query ListLibraryDocumentsByAnchor($anchorType: ScopeAnchorType!, $anchorId: ID!) {
    listLibraryDocumentsByAnchor(anchorType: $anchorType, anchorId: $anchorId) {
      id
      title
      scope {
        anchorType
        anchorId
        competitionYear
        divisionKeys
        applicantTypes
      }
      fileName
      contentType
      sizeBytes
      uploadedAt
      updatedAt
      version
      fileAvailable
    }
  }
`;

// The contestant in-scope, deduped Library_Document set (R7.1, R7.3, R7.5).
export const listMyLibraryDocuments = /* GraphQL */ `
  query ListMyLibraryDocuments {
    listMyLibraryDocuments {
      id
      title
      scope {
        anchorType
        anchorId
        competitionYear
        divisionKeys
        applicantTypes
      }
      fileName
      contentType
      sizeBytes
      uploadedAt
      updatedAt
      version
      fileAvailable
    }
  }
`;

// The single download choke point — authorizes via Computed_Visibility and
// returns a short-lived presigned URL for either kind (R3, R7.4, R9.2).
export const getAnchoredDocumentDownloadUrl = /* GraphQL */ `
  query GetAnchoredDocumentDownloadUrl($documentId: ID!, $kind: AnchoredDocumentKind!) {
    getAnchoredDocumentDownloadUrl(documentId: $documentId, kind: $kind)
  }
`;

// ---------------------------------------------------------------------------
// Tenant foundation (tenant-foundation Task 7.1). `listTenants` is the
// PlatformAdmins-gated operator console read (a fail-closed result wrapper);
// `getTenant` is any-authenticated-user (the self-service join page). All DARK
// until enforcement flips (Task 10).
// ---------------------------------------------------------------------------

export const listTenants = /* GraphQL */ `
  query ListTenants {
    listTenants {
      success
      message
      tenants {
        tenantId
        name
        status
        createdAt
        updatedAt
      }
    }
  }
`;

export const getTenant = /* GraphQL */ `
  query GetTenant($tenantId: ID!) {
    getTenant(tenantId: $tenantId) {
      tenantId
      name
      status
      createdAt
      updatedAt
    }
  }
`;

/**
 * tenant-foundation (Task 7.1 / Req 15.6) — the CALLER's own support windows.
 * Self-scoped (the operator's personId comes from their token sub) and takes no
 * arguments, so it cannot list another operator's grants.
 *
 * Dead windows are returned too, with `state` carrying the refusal reason, so an
 * operator can see that a window they were told about is already closed.
 * `minutesRemaining` is computed server-side and is 0 for anything not live.
 */
export const mySupportGrants = /* GraphQL */ `
  query MySupportGrants {
    mySupportGrants {
      success
      message
      grants {
        tenantId
        operatorPersonId
        expiresAt
        reason
        grantedBy
        grantedAt
        revokedAt
        state
        minutesRemaining
      }
    }
  }
`;
